/**
 * MySQL Credential Form
 *
 * A comprehensive form for configuring MySQL credentials with:
 * - Connection: host, database, port, username, password
 * - Advanced: SSL mode, timeouts, charset
 * Supports section-based rendering for tabbed display
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RefreshCw, Loader2, Database } from 'lucide-react';
import { credentialsApi } from '@/lib/api';
import { toast } from 'sonner';

export interface MySqlFormData {
  // Basic Connection
  host: string;
  database: string;
  username: string;
  password: string;
  port: string;

  // Advanced
  sslMode: string;
  connectTimeout: string;
  allowUserVariables: boolean;
  charset: string;
}

interface MySqlCredentialFormProps {
  formData: MySqlFormData;
  onChange: (data: Partial<MySqlFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
  /** Which section to render - 'connection', 'advanced', or 'all' (default) */
  section?: 'connection' | 'advanced' | 'all';
}

export function MySqlCredentialForm({
  formData,
  onChange,
  isEditing = false,
  section = 'all',
}: MySqlCredentialFormProps) {
  const [fetchingDatabases, setFetchingDatabases] = useState(false);
  const [availableDatabases, setAvailableDatabases] = useState<string[]>([]);

  const handleChange = (field: keyof MySqlFormData, value: any) => {
    onChange({ [field]: value });
  };

  const fetchDatabases = async () => {
    if (!formData.host || !formData.username) {
      toast.error('Please enter host and username first');
      return;
    }

    // Check if password is empty or masked (when editing existing credential)
    const maskedValue = '••••••••';
    if (!formData.password || formData.password === maskedValue) {
      toast.error('Please re-enter your password to fetch databases');
      return;
    }

    setFetchingDatabases(true);
    try {
      const response = await credentialsApi.listDatabases({
        type: 'mysql',
        data: {
          host: formData.host,
          port: formData.port || '3306',
          username: formData.username,
          password: formData.password,
          sslMode: formData.sslMode,
        },
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        setAvailableDatabases(response.data);
        toast.success(`Found ${response.data.length} databases`);
      } else if (Array.isArray(response.data)) {
        toast.info('No databases found');
      } else {
        toast.error(response.data?.error || 'Failed to fetch databases');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'Failed to fetch databases');
    } finally {
      setFetchingDatabases(false);
    }
  };

  // Connection section
  const connectionContent = (
    <div className="space-y-4">
      {/* Host */}
      <div className="space-y-2">
        <Label htmlFor="host" className="text-sm font-medium">
          Host <span className="text-red-500">*</span>
        </Label>
        <Input
          id="host"
          placeholder="localhost or mysql.example.com"
          value={formData.host}
          onChange={(e) => handleChange('host', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Port */}
      <div className="space-y-2">
        <Label htmlFor="port" className="text-sm font-medium">
          Port
        </Label>
        <Input
          id="port"
          placeholder="3306"
          value={formData.port}
          onChange={(e) => handleChange('port', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Database with dropdown */}
      <div className="space-y-2">
        <Label htmlFor="database" className="text-sm font-medium">
          Database <span className="text-red-500">*</span>
        </Label>
        <div className="flex gap-2">
          {availableDatabases.length > 0 ? (
            <Select
              value={formData.database}
              onValueChange={(value) => handleChange('database', value)}
            >
              <SelectTrigger id="database" className="flex-1 font-mono text-sm">
                <SelectValue placeholder="Select database" />
              </SelectTrigger>
              <SelectContent>
                {availableDatabases.map((db) => (
                  <SelectItem key={db} value={db}>
                    <div className="flex items-center gap-2">
                      <Database className="h-3 w-3" />
                      {db}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="database"
              placeholder="mydb"
              value={formData.database}
              onChange={(e) => handleChange('database', e.target.value)}
              className="flex-1 font-mono text-sm"
            />
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={fetchDatabases}
            disabled={fetchingDatabases || !formData.host}
            title="Fetch available databases"
          >
            {fetchingDatabases ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter host and credentials, then click refresh to list available databases
        </p>
      </div>

      {/* Username and Password */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium">
            Username <span className="text-red-500">*</span>
          </Label>
          <Input
            id="username"
            placeholder="root"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            autoComplete="off"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium">
            Password <span className="text-red-500">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder={isEditing ? "Leave unchanged or enter new" : "Enter password"}
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            autoComplete="new-password"
            className="font-mono text-sm"
          />
        </div>
      </div>
    </div>
  );

  // Advanced section
  const advancedContent = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sslMode" className="text-sm font-medium">
          SSL Mode
        </Label>
        <Select
          value={formData.sslMode || 'Preferred'}
          onValueChange={(value) => handleChange('sslMode', value)}
        >
          <SelectTrigger id="sslMode">
            <SelectValue placeholder="Select SSL mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Disabled">Disabled</SelectItem>
            <SelectItem value="Preferred">Preferred (Default)</SelectItem>
            <SelectItem value="Required">Required</SelectItem>
            <SelectItem value="VerifyCA">Verify CA</SelectItem>
            <SelectItem value="VerifyFull">Verify Full</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="connectTimeout" className="text-sm font-medium">
            Connection Timeout (seconds)
          </Label>
          <Input
            id="connectTimeout"
            type="number"
            placeholder="10"
            value={formData.connectTimeout}
            onChange={(e) => handleChange('connectTimeout', e.target.value)}
            min="0"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="charset" className="text-sm font-medium">
            Character Set
          </Label>
          <Input
            id="charset"
            placeholder="utf8mb4"
            value={formData.charset}
            onChange={(e) => handleChange('charset', e.target.value)}
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="allowUserVariables" className="text-sm font-medium">
            Allow User Variables
          </Label>
          <p className="text-xs text-muted-foreground">
            Allow @variable in SQL statements
          </p>
        </div>
        <Switch
          id="allowUserVariables"
          checked={formData.allowUserVariables}
          onCheckedChange={(checked) => handleChange('allowUserVariables', checked)}
        />
      </div>
    </div>
  );

  // Return section-specific content or all
  if (section === 'connection') return connectionContent;
  if (section === 'advanced') return advancedContent;

  // Default: return all content (for backwards compatibility)
  return (
    <div className="space-y-6">
      {connectionContent}
      <div className="border-t pt-4">{advancedContent}</div>
    </div>
  );
}
